import { GstMethod } from "constants/billing";
import {
  getAges,
  getBusinessLabels,
  getCategories,
  getLabels,
  getMasterLabels,
  getMasterRules,
  getMasterRuleSets,
  getRounds,
  getRules,
  getRuleSets,
  getSaleSubTypes,
  getSexes,
  getSpecies,
} from "selectors/root";
import { createSelector } from "reselect";
import uniqBy from "lodash/uniqBy";
import { sortBy, uniq } from "lodash";
import { SundryTemplateLabel } from "constants/sundryTemplates";
import { EMPTY_OBJECT } from "lib";
import { getSelectableBranches } from "selectors/branches";
import { BusinessLabelArchetypeLabels } from "constants/businessLabel";
import { LinkedRootDataType, linkSchema } from "./schema";

export const PRICING_TYPES = [
  "Price per Gross",
  "Price per Kilo",
  "Price per Head",
];

const defaultRelationProperties = [
  { name: "business_id", type: "business" },
  { name: "percentage", type: "percentage" },
  { name: "relationship_effective_commission_percentage", type: "percentage" },
  {
    name: "relationship_effective_commission_per_head",
    type: "currency_cents",
  },
  { name: "name", type: "text" },
  {
    name: "is_gst_registered",
    set: [
      ["True", true],
      ["False", false],
    ],
    type: "boolean",
  },
  {
    name: "should_charge_gst",
    set: [
      ["True", true],
      ["False", false],
    ],
    type: "gst_applicability_boolean",
  },
];

function schemaReducer(
  ageByIdLookup: StateModelById<Age>,
  categoryByIdLookup: StateModelById<Category>,
  roundByIdLookup: StateModelById<Round>,
  sexByIdLookup: StateModelById<Sex>,
  speciesByIdLookup: StateModelById<Species>,
  saleSubTypeByIdLookup: StateModelById<SaleSubType>,
  labelOrMasterLabelByIdLookup: StateModelById<Label | MasterLabel>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Sundry template labels from API
  sundryTemplateLabels: any,
  selectableBranches: StateModelById<Branch>,
  businessLabelByIdLookup: StateModelById<BusinessLabel>,
): LinkedRootDataType {
  // Add the static list of label values to both the lot and the manual adjustments.
  // On a master rule, this is selectable from the MASTER LABEL list.
  // On a custom rule, this is selectable from the DEPLOYMENT LABEL list.
  // Both are stored as string values, as compared to id driven relationships.

  const labelOptions = uniq(
    Object.values(labelOrMasterLabelByIdLookup).map(label => label.name),
  ).map(name => [name, name]);

  // For a manual adjustment/sundry also include the sundry template labels for master rules.
  const manualAdjustmentLabelOptions = [
    ...labelOptions,
    ...Object.keys(sundryTemplateLabels).map(name => [name, name]),
  ];

  // Business label options: match by label name (deployment/saleyard-scoped labels).
  const businessLabelOptions = uniq(
    Object.values(businessLabelByIdLookup).map(
      (label: BusinessLabel) => label.name,
    ),
  ).map(name => [name, name]);

  // Business label archetype options: static list of valid archetypes.
  const businessLabelArchetypeOptions = Object.entries(
    BusinessLabelArchetypeLabels,
  ).map(([value, displayName]) => [displayName, value]);

  const schema: LinkedRootDataType = linkSchema(
    sortBy(
      [
        {
          id: "age",
          idAttribute: "id:int",
          path: "ages",
          singular: "Age",
          plural: "Ages",
          properties: [
            {
              name: "name",
              type: "text",
              set: Object.values(ageByIdLookup)
                .sort((a, b) => a.order - b.order)
                .map(age => [age.name, age.name]),
            },
          ],
          possessive: "Age's",
        },
        {
          id: "animal",
          idAttribute: "id:uuid",
          path: "animals",
          singular: "Animal",
          plural: "Animals",
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "lot", via: "rel:lot" },
            { id: "sale", via: "rel:sale" },
          ],
          properties: [
            { name: "calculated_weight_grams", type: "weight_grams" },
            { name: "eid", type: "text" },
            {
              name: "is_weighed",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            { name: "total_weight_grams", type: "weight_grams" },
          ],
          possessive: "Animal's",
        },
        {
          id: "breed",
          idAttribute: "id:int",
          path: "breeds",
          singular: "Breed",
          plural: "Breeds",
          properties: [{ name: "name", type: "text" }],
          possessive: "Breed's",
        },
        {
          id: "buyer",
          idAttribute: "id:auto_id",
          path: "buyers",
          singular: "Buyer",
          plural: "Buyers",
          properties: [
            { name: "business_id", type: "business" },
            { name: "name", type: "business_name" },
            { name: "buyer_way", type: "text" },
            { name: "registration_number", type: "text" },
            {
              name: "is_hobby_farmer",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "is_meat_buyer",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_debtor_insurance",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_transit_insurance",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_transit_insurance_for_sales",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_transit_insurance_for_purchases",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "should_charge_gst",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "gst_applicability_boolean",
            },
            {
              name: "branch",
              type: "branch",
              set: Object.values(selectableBranches).map(branch => [
                branch.name,
                branch.name,
              ]),
            },
            {
              name: "business_labels",
              type: "text",
              set: businessLabelOptions,
            },
            {
              name: "business_labels_archetype",
              type: "text",
              set: businessLabelArchetypeOptions,
            },
          ],
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
          ],
          manyRelations: [
            {
              id: "buyers_purchase_rebate_agent",
              via: "buyers_purchase_rebate_agents",
              name: "Buyer's Purchase Rebate Agents",
            },
          ],
          possessive: "Buyer's",
        },
        {
          id: "buyers_purchase_rebate_agent",
          idAttribute: "id:auto_id",
          path: "buyers_purchase_rebate_agents",
          singular: "Buyer's Purchase Rebate Agent",
          plural: "Buyer's Purchase Rebate Agents",
          properties: defaultRelationProperties,
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
          ],
          manyRelations: [],
          possessive: "Buyer's Purchase Rebate Agent's",
        },
        {
          id: "carrier",
          idAttribute: "id:auto_id",
          path: "carriers",
          singular: "Carrier",
          plural: "Carriers",
          properties: [
            { name: "business_id", type: "business" },
            { name: "percentage", type: "percentage" },
            { name: "name", type: "text" },
            {
              name: "is_gst_registered",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
          ],
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
          ],
          manyRelations: [
            {
              id: "manual_adjustment",
              via: "to_manual_adjustment",
              name: "To Sundries",
              revName: "Carriers (To Business)",
            },
            {
              id: "manual_adjustment",
              via: "from_manual_adjustment",
              name: "From Sundries",
              revName: "Carriers (From Business)",
            },
          ],
          possessive: "Carrier's",
        },
        {
          id: "consignment",
          idAttribute: "id:uuid",
          path: "consignments",
          singular: "Consignment",
          plural: "Consignments",
          properties: [
            { name: "total_hd_nominated", type: "integer" },
            { name: "billing_reference", type: "text" },
          ],
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
            { id: "vendor", via: "rel:vendor" },
            { id: "vendor_property", via: "rel:vendor_property" },
            {
              id: "consigning_deployment",
              via: "rel:consigned_from",
              name: "Consigned From (Agency)",
              uniq: "consigned_from_consigning_deployment",
              revName: "Consignments (Consigned From)",
            },
            {
              id: "consigning_deployment",
              via: "rel:consigned_to",
              name: "Consigned To (Agency)",
              uniq: "consigned_to_consigning_deployment",
              revName: "Consignments (Consigned To)",
            },
          ],
          manyRelations: [
            {
              id: "outside_agent",
              via: "outside_agents",
              name: "Outside Agents",
            },
            {
              id: "stock_agent",
              via: "stock_agents",
              name: "Stock Agents",
            },
            {
              id: "rebate_agent",
              via: "rebate_agents",
              name: "Rebate Agents",
            },
            {
              id: "livestock_consultant",
              via: "livestock_consultants",
              name: "Livestock Consultants",
            },
          ],
          possessive: "Consignment's",
        },
        {
          id: "deployment",
          idAttribute: "id:uuid",
          path: "deployments",
          singular: "Deployment (Agency)",
          plural: "Deployments (Agencies)",
          properties: [
            { name: "business_id", type: "business" },
            {
              name: "is_gst_registered",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            { name: "name", type: "business_name" },
            { name: "agent_ref", type: "text" },
            { name: "saleyard_ref", type: "text" },
            { name: "code", type: "text" },
            { name: "channel", type: "text" },
            { name: "bpay_biller_code", type: "text" },
            {
              name: "should_charge_gst",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "gst_applicability_boolean",
            },
            {
              name: "is_hobby_farmer",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_debtor_insurance",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_transit_insurance",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            { name: "debtor_insurance_provider_business_id", type: "business" },
            {
              name: "transit_insurance_for_purchases_provider_business_id",
              type: "business",
            },
            {
              name: "transit_insurance_for_sales_provider_business_id",
              type: "business",
            },
            {
              name: "has_transit_insurance_for_sales",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_transit_insurance_for_purchases",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "default_commission",
              type: "currency_cents",
            },
            {
              name: "default_debtor_insurance",
              type: "percentage",
            },
            {
              name: "default_interstate_transit_insurance",
              type: "percentage",
            },
            {
              name: "default_transit_insurance",
              type: "percentage",
            },
            // Transit Insurance for Sales
            {
              name: "default_transit_insurance_for_sales",
              displayName: "Transit Insurance for Sales - Client Rate",
              type: "percentage",
            },
            {
              name: "default_provider_transit_insurance_for_sales",
              displayName: "Transit Insurance for Sales - Provider Rate",
              type: "percentage",
            },
            {
              name: "default_admin_rate_transit_insurance_for_sales",
              displayName: "Transit Insurance for Sales - Admin Rate",
              type: "percentage",
            },
            // Transit Insurance for Sales - Extended
            {
              name: "default_extended_transit_insurance_for_sales",
              displayName:
                "Transit Insurance for Sales - Extended - Client Rate",
              type: "percentage",
            },
            {
              name: "default_provider_extended_transit_insurance_for_sales",
              displayName:
                "Transit Insurance for Sales - Extended - Provider Rate",
              type: "percentage",
            },
            {
              name: "default_admin_rate_extended_transit_insurance_for_sales",
              displayName:
                "Transit Insurance for Sales - Extended - Admin Rate",
              type: "percentage",
            },
            // Transit Insurance for Purchases
            {
              name: "default_transit_insurance_for_purchases",
              displayName: "Transit Insurance for Purchases - Client Rate",
              type: "percentage",
            },
            {
              name: "default_provider_transit_insurance_for_purchases",
              displayName: "Transit Insurance for Purchases - Provider Rate",
              type: "percentage",
            },
            {
              name: "default_admin_rate_transit_insurance_for_purchases",
              displayName: "Transit Insurance for Purchases - Admin Rate",
              type: "percentage",
            },
            // Transit Insurance for Purchases - Extended
            {
              name: "default_extended_transit_insurance_for_purchases",
              displayName:
                "Transit Insurance for Purchases - Extended - Client Rate",
              type: "percentage",
            },
            {
              name: "default_provider_extended_transit_insurance_for_purchases",
              displayName:
                "Transit Insurance for Purchases - Extended - Provider Rate",
              type: "percentage",
            },
            {
              name: "default_admin_rate_extended_transit_insurance_for_purchases",
              displayName:
                "Transit Insurance for Purchases - Extended - Admin Rate",
              type: "percentage",
            },

            {
              name: "portion_of_sale_by_quantity_exc_progeny",
              type: "percentage",
            },
            {
              name: "portion_of_sale_by_agencies_involved",
              type: "percentage",
            },
            {
              name: "vendor_branch",
              type: "branch",
              set: Object.values(selectableBranches).map(branch => [
                branch.name,
                branch.name,
              ]),
            },
          ],
          relations: [{ id: "sale", via: "rel:sale" }],
          possessive: "Deployment (Agency)'s",
        },
        {
          id: "consigning_deployment",
          idAttribute: "id:int",
          path: "consigning_deployments",
          singular: "Consigning Deployment (Agency)",
          plural: "Consigning Deployments (Agencies)",
          properties: [
            { name: "business_id", type: "business" },
            {
              name: "is_gst_registered",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            { name: "name", type: "business_name" },
            { name: "agent_ref", type: "text" },
            { name: "saleyard_ref", type: "text" },
            { name: "code", type: "text" },
            { name: "channel", type: "text" },
            {
              name: "should_charge_gst",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "gst_applicability_boolean",
            },
            {
              name: "is_hobby_farmer",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
          ],
          possessive: "Consigning Deployment (Agency)'s",
        },
        {
          id: "lot",
          idAttribute: "id:uuid",
          path: "lots",
          singular: "Sale Lot",
          plural: "Sale Lots",
          relations: [
            { id: "age", via: "rel:age" },
            { id: "buyer", via: "rel:buyer" },
            { id: "buyer_property", via: "rel:buyer_property" },
            { id: "breed", via: "rel:breed" },
            { id: "deployment", via: "rel:deployment" },
            { id: "consignment", via: "rel:consignment", revName: "Sale Lots" },
            {
              id: "vendor",
              via: "rel:vendor",
            },
            { id: "round", via: "rel:round" },
            { id: "sale", via: "rel:sale" },
            { id: "sex", via: "rel:sex" },
            { id: "species", via: "rel:species" },
            { id: "yard", via: "rel:yard" },
          ],
          properties: [
            {
              name: "allocation_percentage",
              type: "percentage",
            },
            { name: "average_weight_grams", type: "weight_grams" },

            { name: "cents_per_head", type: "currency_cents" },
            {
              name: "commercial_category",
              type: "text",
              set: Object.values(categoryByIdLookup)
                .sort((a, b) => a.order - b.order)
                .map(category => [category.name, category.name]),
            },
            {
              name: "dead_count",
              type: "integer",
            },
            {
              name: "effective_buyers_premium_cents",
              type: "currency_cents",
            },
            {
              name: "effective_listing_fee_cents",
              type: "currency_cents",
            },
            {
              name: "effective_vendor_commission_cents",
              type: "currency_cents",
            },
            {
              name: "is_gst_exempt",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "is_no_sale",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "is_sold",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "is_skins",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "is_weighed",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "labels",
              type: "list",
              set: labelOptions,
            },
            {
              name: "no_tag_count",
              type: "integer",
            },
            {
              name: "non_reader_count",
              type: "integer",
            },
            {
              name: "should_charge_gst",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "gst_applicability_boolean",
            },
            { name: "total_price_cents", type: "currency_cents" },
            { name: "total_weight_grams", type: "weight_grams" },
            {
              name: "total_hd_exc_progeny",
              type: "integer",
            },
            {
              name: "total_hd_inc_progeny",
              type: "integer",
            },
            {
              name: "total_hd_progeny",
              type: "integer",
            },
            {
              name: "total_tags_used",
              type: "integer",
            },
            {
              name: "pricing_type",
              type: "text",
              set: PRICING_TYPES.map(pricingType => [pricingType, pricingType]),
            },
            {
              name: "vendor_branch",
              type: "branch",
              set: Object.values(selectableBranches).map(branch => [
                branch.name,
                branch.name,
              ]),
            },
            {
              name: "buyer_branch",
              type: "buyer_branch",
              set: Object.values(selectableBranches).map(branch => [
                branch.name,
                branch.name,
              ]),
            },
          ],
          manyRelations: [
            {
              id: "buyers_purchase_rebate_agent",
              via: "buyers_purchase_rebate_agents",
              name: "Buyer's Purchase Rebate Agents",
            },
            {
              id: "outside_agent",
              via: "outside_agents",
              name: "Outside Agents",
            },
            {
              id: "stock_agent",
              via: "stock_agents",
              name: "Stock Agents",
            },
            {
              id: "rebate_agent",
              via: "rebate_agents",
              name: "Rebate Agents",
            },
            {
              id: "livestock_consultant",
              via: "livestock_consultants",
              name: "Livestock Consultants",
            },
          ],
          possessive: "Sale Lot's",
        },
        {
          id: "manual_adjustment_business",
          idAttribute: "id:uuid",
          path: "manual_adjustment_businesses",
          singular: "Sundry Business",
          plural: "Sundry Businesses",
          properties: [
            { name: "business_id", type: "business" },
            { name: "name", type: "text" },
            {
              name: "branch",
              type: "branch",
              set: Object.values(selectableBranches).map(branch => [
                branch.name,
                branch.name,
              ]),
            },
            {
              name: "should_charge_gst",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "gst_applicability_boolean",
            },
            {
              name: "is_gst_registered",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
          ],
          relations: [],
          manyRelations: [],
          possessive: "Sundry Business's",
        },
        {
          id: "manual_adjustment",
          idAttribute: "id:uuid",
          path: "manual_adjustments",
          singular: "Sundry",
          plural: "Sundries",
          properties: [
            { name: "from_business_id", type: "business" },
            { name: "to_business_id", type: "business" },
            { name: "subtotal_cents", type: "currency_cents" },
            { name: "gl_code", type: "gl_code" },
            { name: "gst_cents", type: "currency_cents" },
            { name: "tax_type", type: "tax_type" },
            { name: "total_cents", type: "currency_cents" },
            { name: "labels", type: "list", set: manualAdjustmentLabelOptions },
            {
              name: "gst_method",
              set: [
                ["Fixed", GstMethod.GST_FIXED],
                ["Inclusive", GstMethod.GST_INCLUSIVE],
                ["Exclusive", GstMethod.GST_EXCLUSIVE],
                ["Exempt", GstMethod.GST_EXEMPT],
              ],
              type: "boolean",
            },
          ],
          relations: [
            { id: "yard", via: "rel:yard" },
            { id: "deployment", via: "rel:deployment" },
            {
              id: "manual_adjustment_business",
              name: "From Business",
              via: "rel:from_business",
              uniq: "from_manual_adjustment_business",
              revName: "Sundries (From Business)",
            },
            {
              id: "manual_adjustment_business",
              via: "rel:to_business",
              name: "To Business",
              uniq: "to_manual_adjustment_business",
              revName: "Sundries (To Business)",
            },
          ],
          manyRelations: [],
          possessive: "Sundry's",
        },
        {
          id: "outside_agent",
          idAttribute: "id:auto_id",
          path: "outside_agents",
          singular: "Outside Agent",
          plural: "Outside Agents",
          properties: defaultRelationProperties,
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
          ],
          possessive: "Outside Agent's",
        },
        {
          id: "round",
          idAttribute: "id:int",
          path: "rounds",
          singular: "Round",
          plural: "Rounds",
          properties: [
            {
              name: "name",
              type: "text",
              set: Object.values(roundByIdLookup)
                .sort((a, b) => a.order - b.order)
                .map(round => [round.name, round.name]),
            },
          ],
          relations: [{ id: "sale", via: "rel:sale" }],
          possessive: "Round's",
        },
        {
          id: "sale",
          idAttribute: "id:uuid",
          path: "sales",
          singular: "Sale",
          plural: "Sales",
          properties: [
            { name: "code", type: "text" },
            { name: "type", type: "text" },
            {
              name: "sub_type",
              type: "text",
              set: uniqBy(Object.values(saleSubTypeByIdLookup), "name")
                .sort((a, b) => a.order - b.order)
                .map(subType => {
                  const speciesLabelText = Object.values(saleSubTypeByIdLookup)
                    .filter(sub => sub.name === subType.name)
                    .map(sub => speciesByIdLookup[sub.speciesId]?.name)
                    .sort()
                    .join("/");
                  return [
                    `${speciesLabelText} - ${subType.name}`,
                    subType.name,
                  ];
                }),
            },
            { name: "date", type: "text" },
            {
              name: "is_stud_sale",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
          ],
          possessive: "Sale's",
        },
        {
          id: "sex",
          idAttribute: "id:int",
          path: "sexes",
          singular: "Sex",
          plural: "Sexes",
          properties: [
            {
              name: "name",
              type: "text",
              set: Object.values(sexByIdLookup)
                .sort((a, b) => a.order - b.order)
                .map(sex => [sex.name, sex.name]),
            },
          ],
          possessive: "Sex's",
        },
        {
          id: "species",
          idAttribute: "id:auto_id",
          path: "species",
          singular: "Species",
          plural: "Species",
          properties: [
            {
              name: "name",
              type: "text",
              set: Object.values(speciesByIdLookup)
                .sort((a, b) => a.order - b.order)
                .map(species => [species.name, species.name]),
            },
            {
              name: "default_pricing_type",
              type: "text",
            },
          ],
          relations: [{ id: "sale", via: "rel:sale" }],
          possessive: "Species's",
        },
        {
          id: "stock_agent",
          idAttribute: "id:auto_id",
          path: "stock_agents",
          singular: "Stock Agent",
          plural: "Stock Agents",
          properties: defaultRelationProperties,
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
          ],
          manyRelations: [],
          possessive: "Stock Agent's",
        },
        {
          id: "rebate_agent",
          idAttribute: "id:auto_id",
          path: "rebate_agents",
          singular: "Rebate Agent",
          plural: "Rebate Agents",
          properties: defaultRelationProperties,
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
          ],
          manyRelations: [],
          possessive: "Rebate Agent's",
        },
        {
          id: "livestock_consultant",
          idAttribute: "id:auto_id",
          path: "livestock_consultants",
          singular: "Livestock Consultant",
          plural: "Livestock Consultants",
          properties: defaultRelationProperties,
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
          ],
          manyRelations: [],
          possessive: "Livestock Consultant's",
        },
        {
          id: "vendor",
          idAttribute: "id:auto_id",
          path: "vendors",
          singular: "Vendor",
          plural: "Vendors",
          properties: [
            { name: "business_id", type: "business" },
            {
              name: "is_gst_registered",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            { name: "name", type: "business_name" },
            {
              name: "is_hobby_farmer",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_debtor_insurance",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "effective_commission_percentage",
              type: "percentage",
            },
            {
              name: "effective_commission_per_head",
              type: "currency_cents",
            },
            {
              name: "has_transit_insurance",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_transit_insurance_for_sales",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "has_transit_insurance_for_purchases",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "boolean",
            },
            {
              name: "should_charge_gst",
              set: [
                ["True", true],
                ["False", false],
              ],
              type: "gst_applicability_boolean",
            },
            {
              name: "branch",
              type: "branch",
              set: Object.values(selectableBranches).map(branch => [
                branch.name,
                branch.name,
              ]),
            },
            {
              name: "business_labels",
              type: "text",
              set: businessLabelOptions,
            },
            {
              name: "business_labels_archetype",
              type: "text",
              set: businessLabelArchetypeOptions,
            },
          ],
          relations: [
            { id: "deployment", via: "rel:deployment" },
            { id: "sale", via: "rel:sale" },
            { id: "vendor_property", via: "rel:vendor_property" },
          ],
          manyRelations: [
            {
              id: "outside_agent",
              via: "outside_agents",
              name: "Outside Agents",
            },
            {
              id: "stock_agent",
              via: "stock_agents",
              name: "Stock Agents",
            },
            {
              id: "rebate_agent",
              via: "rebate_agents",
              name: "Rebate Agents",
            },
            {
              id: "livestock_consultant",
              via: "livestock_consultants",
              name: "Livestock Consultants",
            },
          ],
          possessive: "Vendor's",
        },
        {
          id: "yard",
          idAttribute: "id:int",
          path: "yards",
          singular: "Sale Yard",
          plural: "Sale Yards",
          properties: [
            { name: "name", type: "business" },
            { name: "name", type: "text" },
          ],
          possessive: "Sale Yard's",
        },
        {
          id: "vendor_property",
          idAttribute: "id:int",
          path: "vendor_properties",
          singular: "Vendor Property",
          plural: "Vendor Properties",
          properties: [
            { name: "pic", type: "text" },
            { name: "name", type: "text" },
          ],
          possessive: "Vendor Property's",
        },
        {
          id: "buyer_property",
          idAttribute: "id:int",
          path: "buyer_properties",
          singular: "Buyer Property",
          plural: "Buyer Properties",
          properties: [
            { name: "pic", type: "text" },
            { name: "name", type: "text" },
          ],
          possessive: "Buyer Property's",
        },
      ],
      "id",
    ),
  );

  return schema;
}

export const selectRuleLivestockSaleFieldSchema = createSelector(
  [
    getAges,
    getCategories,
    getRounds,
    getSexes,
    getSpecies,
    getSaleSubTypes,
    getLabels,
    () => SundryTemplateLabel,
    getSelectableBranches,
    getBusinessLabels,
  ],
  schemaReducer,
);

export const selectMasterRuleLivestockSaleFieldSchema = createSelector(
  [
    getAges,
    getCategories,
    getRounds,
    getSexes,
    getSpecies,
    getSaleSubTypes,
    getMasterLabels,
    () => EMPTY_OBJECT,
    getSelectableBranches,
    getBusinessLabels,
  ],
  schemaReducer,
);

type RuleId = string;

export interface RuleChain {
  // The Id of the Rule
  id: RuleId;

  // The literal input field of the rule
  inputField: string | null;

  // The literal input field of the ruleSet
  ruleSetInputField: string | null;

  // A list of input field in the prerequisite tree for this rule, null when not computed
  // null when not yet computed
  inputPathParts: string[] | null;

  // The chain of prerequisite rules and their input fields
  // null when not yet computed
  prerequisites: RuleChain[] | null;
}

// This function is used to assemble the input path parts for a rule chain.
// Throws an `Error` on circular references
// Returns the path parts in reverse order
function assembleInputPathParts(
  ruleChain: RuleChain,
  ruleSetInputField: string | null,
  inputField: string | null,
  visitedRules: RuleId[] = [],
): string[] {
  if (visitedRules.includes(ruleChain.id)) {
    throw Error(
      `Circular reference detected in rule chain for rule ${ruleChain.id}, ${JSON.stringify(visitedRules)}`,
    );
  }
  visitedRules.push(ruleChain.id);

  // When the input field is null, assume the parent's input field type
  // The RuleSet input field should only be used when the rule doesn't have any prerequisite rules
  // in that case, the Rule's input field hangs off of the RuleSet's input field
  // constructed in reverse order for reduced complexity

  const parts = [];
  parts.push(inputField);
  if (ruleSetInputField && ruleChain.prerequisites.length === 0) {
    parts.push(ruleSetInputField);
  } else if (ruleChain.prerequisites.length > 0) {
    // All prerequisite rules must be based on the same datatype, in which case we only need to use the first one
    const firstPrerequisiteChain = ruleChain.prerequisites[0];
    if (firstPrerequisiteChain?.inputPathParts === null) {
      // The prerequisite rule's input field type is not yet computed, recursively compute it
      return parts.concat(
        assembleInputPathParts(
          firstPrerequisiteChain,
          firstPrerequisiteChain.ruleSetInputField,
          firstPrerequisiteChain.inputField,
          visitedRules,
        ),
      );
    }
    // The prerequisite rule's input field type is already computed, so we can use it directly
    return parts.concat(firstPrerequisiteChain?.inputPathParts);
  }
  return parts;
}

function ruleChainReducer<
  RuleVariant extends Rule | MasterRule,
  RuleSetVariant extends RuleSet | MasterRuleSet,
>(
  ruleByIdLookup: Record<RuleId, RuleVariant>,
  ruleSetByIdLookup: Record<RuleId, RuleSetVariant>,
): Record<RuleId, RuleChain> {
  const ruleChainById = Object.values(ruleByIdLookup).reduce(
    (acc, rule: RuleVariant) => {
      const ruleSet = ruleSetByIdLookup[rule.rule_set_id] || null;
      const ruleSetInputField = ruleSet?.input_field || null;
      const inputField = rule.input_field;
      const prerequisites = rule.prerequisite_rule_ids.map(
        prerequisiteRuleId => {
          // Placeholder for the prerequisite rule, these details will filled in on the second pass
          const prerequisiteRuleChain: RuleChain = {
            id: prerequisiteRuleId,
            inputField: null,
            ruleSetInputField: null,
            inputPathParts: null,
            prerequisites: [],
          };
          return prerequisiteRuleChain;
        },
      );

      acc[rule.id] = {
        id: rule.id,
        inputField,
        inputPathParts: null,
        ruleSetInputField,
        prerequisites,
      };
      return acc;
    },
    {} as Record<RuleId, RuleChain>,
  );

  // Perform a second pass to fill in the prerequisite rule details
  Object.values(ruleChainById).forEach(ruleChain => {
    ruleChain.prerequisites.forEach((prerequisiteRuleChain, index, arr) => {
      arr[index] = ruleChainById[prerequisiteRuleChain.id];
    });
  });

  // Perform a third pass to fill in the fill input path parts
  Object.values(ruleChainById).forEach(ruleChain => {
    ruleChain.inputPathParts = assembleInputPathParts(
      ruleChain,
      ruleChain.ruleSetInputField,
      ruleChain.inputField,
    );
  });

  // All the rule chains were constructed in reverse, undo that
  Object.values(ruleChainById).forEach(ruleChain => {
    ruleChain.inputPathParts = ruleChain.inputPathParts.reverse();
  });

  return ruleChainById;
}

export const selectPrerequisiteRuleChainByRuleIdLookup = createSelector(
  [getRules, getRuleSets],
  ruleChainReducer<Rule, RuleSet>,
);

export const getPrerequisiteRuleChainByRuleId =
  (ruleId: string) => (state: RootState) =>
    selectPrerequisiteRuleChainByRuleIdLookup(state)[ruleId] || null;

export const selectPrerequisiteMasterRuleChainByMasterRuleIdLookup =
  createSelector(
    [getMasterRules, getMasterRuleSets],
    ruleChainReducer<MasterRule, MasterRuleSet>,
  );

export const getPrerequisiteMasterRuleChainByMasterRuleId =
  (masterRuleId: string) => (state: RootState) =>
    selectPrerequisiteMasterRuleChainByMasterRuleIdLookup(state)[
      masterRuleId
    ] || null;
